import React from "react";
import { OPERATOR_LABELS, isListOperator, isRangeOperator } from "./helpers";

/**
 * Picks a sensible placeholder for the value input given the operator and type.
 * @param {string} operator the selected operator.
 * @param {string} [type] the field's logical type.
 * @returns {string}
 */
function valuePlaceholder(operator, type) {
  if (isListOperator(operator)) return "a, b, c";
  if (isRangeOperator(operator)) return "from";
  if (type === "date") return "YYYY-MM-DD";
  return "value";
}

/**
 * Chooses the HTML input type for a value: a native date/number picker for those
 * field types, but plain text for the `in` operator (a comma-separated list).
 * @param {string} operator the selected operator.
 * @param {string} [type] the field's logical type.
 * @returns {"date"|"number"|"text"}
 */
function inputType(operator, type) {
  if (isListOperator(operator)) return "text";
  if (type === "date") return "date";
  if (type === "number") return "number";
  return "text";
}

/**
 * One filter clause: field → operator → value(s). Operators offered come from
 * the chosen field's metadata; a range operator reveals a second value input.
 * @param {object} props
 * @param {number} props.index the row's position (for change/remove callbacks).
 * @param {object} props.filter the filter row ({ field, operator, value, value2 }).
 * @param {Array<object>} props.fields the module's field metadata.
 * @param {(index: number, patch: object) => void} props.onChange updates the row.
 * @param {(index: number) => void} props.onRemove removes the row.
 * @returns {JSX.Element}
 */
export default function FilterRow({ index, filter, fields, onChange, onRemove }) {
  const field = fields.find((f) => f.key === filter.field);
  const operators = field ? field.operators : [];
  const range = isRangeOperator(filter.operator);

  return (
    <div className="qb-filter-row">
      <select
        className="qb-select"
        aria-label="Field"
        value={filter.field}
        onChange={(e) =>
          onChange(index, {
            field: e.target.value,
            operator: "=",
            value: "",
            value2: "",
          })
        }
      >
        {fields.map((f) => (
          <option key={f.key} value={f.key}>
            {f.label}
          </option>
        ))}
      </select>

      <select
        className="qb-select"
        aria-label="Operator"
        value={filter.operator}
        onChange={(e) => onChange(index, { operator: e.target.value })}
      >
        {operators.map((op) => (
          <option key={op} value={op}>
            {OPERATOR_LABELS[op] || op}
          </option>
        ))}
      </select>

      {field?.type === "boolean" ? (
        <select
          className="qb-select"
          aria-label="Value"
          value={filter.value}
          onChange={(e) => onChange(index, { value: e.target.value })}
        >
          <option value="">Select…</option>
          <option value="1">True</option>
          <option value="0">False</option>
        </select>
      ) : field?.options?.length && !range ? (
        <select
          className="qb-select"
          aria-label="Value"
          value={filter.value}
          onChange={(e) => onChange(index, { value: e.target.value })}
        >
          <option value="">Select…</option>
          {field.options.map((opt) => (
            <option key={opt} value={opt}>
              {opt}
            </option>
          ))}
        </select>
      ) : (
        <input
          className="qb-input"
          aria-label="Value"
          type={inputType(filter.operator, field?.type)}
          value={filter.value}
          placeholder={valuePlaceholder(filter.operator, field?.type)}
          onChange={(e) => onChange(index, { value: e.target.value })}
        />
      )}

      {range && (
        <input
          className="qb-input"
          aria-label="Second value"
          type={inputType(filter.operator, field?.type)}
          value={filter.value2}
          placeholder="to"
          onChange={(e) => onChange(index, { value2: e.target.value })}
        />
      )}

      <button
        type="button"
        className="qb-icon-btn"
        aria-label="Remove filter"
        onClick={() => onRemove(index)}
      >
        ×
      </button>
    </div>
  );
}
